import type {
  MatchEvent,
  MatchSummary,
  MatchOptions,
  MatchSide,
  MatchSimulation,
} from "./types";
import { getPlayerRoster } from "../game/playerRosters";

const TEAMMATES = [
  "Bruno Leal", "Hugo Mora", "Iker Vidal", "Mario Soler", "Dani Lozano",
  "Leo Pardo", "Joel Serra", "Nico Valdés", "Iván Costa", "Pablo Navas",
  "Marcos Rey", "Enzo Cano", "Ángel Ríos", "Sergio Luna",
] as const;

const OPPONENTS = [
  "Samuel Vega", "Adrián Blanco", "Eric Santos", "Gabi Flores", "Raúl Nieto",
  "Óscar Galán", "Víctor Mena", "Martín Peña", "Lucas Rojo", "Álvaro Casas",
  "Darío Silva", "Mateo Pastor", "Jorge León", "Rubén Sáez",
] as const;

interface MatchParticipant { name: string; isUser: boolean }

type Random = () => number;

const PLAYER_PRODUCTION_BY_STAGE = {
  MCO: [
    { goalsPer90: .25, assistsPer90: .50 },
    { goalsPer90: .30, assistsPer90: .60 },
    { goalsPer90: .35, assistsPer90: .70 },
    { goalsPer90: .40, assistsPer90: .80 },
    { goalsPer90: .45, assistsPer90: .90 },
  ],
  DC: [
    { goalsPer90: .70, assistsPer90: .15 },
    { goalsPer90: .78, assistsPer90: .18 },
    { goalsPer90: .90, assistsPer90: .21 },
    { goalsPer90: 1.00, assistsPer90: .24 },
    { goalsPer90: 1.10, assistsPer90: .27 },
  ],
} as const;

const EXPECTED_OVR_BY_STAGE = [60, 67, 74, 81, 88] as const;

function playerProductionProfile(options: MatchOptions) {
  const stageIndex = Math.max(0, Math.min(4, Math.floor(options.careerStage ?? 3) - 1));
  const position = options.player.position === "DC" ? "DC" : "MCO";
  const base = PLAYER_PRODUCTION_BY_STAGE[position][stageIndex]!;
  const overallDelta = (options.playerOverall ?? EXPECTED_OVR_BY_STAGE[stageIndex]!) - EXPECTED_OVR_BY_STAGE[stageIndex]!;
  return position === "DC"
    ? {
        goalsPer90: Math.max(.35, Math.min(1.35, base.goalsPer90 + overallDelta * .02)),
        assistsPer90: Math.max(.08, Math.min(.45, base.assistsPer90 + overallDelta * .008)),
      }
    : {
        goalsPer90: Math.max(.12, Math.min(.70, base.goalsPer90 + overallDelta * .01)),
        assistsPer90: Math.max(.25, Math.min(1.15, base.assistsPer90 + overallDelta * .018)),
      };
}

function sampleProduction(expected: number, random: Random): number {
  const chance = Math.max(0, Math.min(1, expected / 2));
  return Number(random() < chance) + Number(random() < chance);
}

function mulberry32(seed: number): Random {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function pick<T>(values: readonly T[], random: Random): T {
  return values[Math.floor(random() * values.length)]!;
}

function shuffle<T>(values: readonly T[], random: Random): T[] {
  const copy = [...values];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    [copy[index], copy[other]] = [copy[other]!, copy[index]!];
  }
  return copy;
}

function durationFor(options: MatchOptions): number {
  if (options.durationMs) return Math.max(1_000, options.durationMs);
  if (options.intensity === "boss") return 19_000;
  if (options.intensity === "derby") return 17_000;
  return 14_500;
}

function createTeam(
  side: MatchSide,
  options: MatchOptions,
  random: Random,
): MatchParticipant[] {
  const home = side === "home";
  const source = home
    ? options.homeSquadNames ?? getPlayerRoster(options.homeTeam) ?? TEAMMATES
    : options.awaySquadNames ?? getPlayerRoster(options.opponent) ?? OPPONENTS;
  const fallback = home ? TEAMMATES : OPPONENTS;
  const squad = source.length >= 11 ? source : fallback;
  // Las plantillas sincronizadas están ordenadas por posición: el primer nombre
  // siempre es un guardameta. Se barajan únicamente los jugadores de campo.
  const names = [squad[0]!, ...shuffle(squad.slice(1), random).slice(0, 10)];

  while (names.length < 11) names.push(`${home ? "Local" : "Visitante"} ${names.length + 1}`);
  if (home && (options.playerMinutes ?? 90) > 0) names[options.player.position === "DC" ? 9 : 6] = options.player.name;

  return names.map((name) => ({ name, isUser: home && name === options.player.name }));
}

export function buildMatchSimulation(options: MatchOptions): MatchSimulation {
  const random = mulberry32(options.seed ?? hash(options.matchId));
  const production = playerProductionProfile(options);
  const lateReactionMinute = (options.intensity === "boss" || options.intensity === "derby") && options.expectedResult === "loss"
    ? 45 + Math.floor(Math.sqrt(random()) * 36)
    : undefined;
  const participationFactor = Math.max(0, Math.min(1, (options.playerMinutes ?? 90) / 90));
  let homeGoals: number;
  let awayGoals: number;

  if (options.expectedResult === "win") {
    homeGoals = 1 + Math.floor(random() * 3);
    awayGoals = Math.floor(random() * homeGoals);
  } else if (options.expectedResult === "draw") {
    homeGoals = Math.floor(random() * 3);
    awayGoals = homeGoals;
  } else if (options.intensity === "boss" || options.intensity === "derby") {
    // La reacción tardía solo se ofrece si el rival gana por la mínima.
    // Mantener este marcador estable permite pausarla en el minuto 45 sin revelar
    // un resultado futuro incompatible con la decisión del jugador.
    homeGoals = 0;
    awayGoals = 1;
  } else {
    homeGoals = Math.floor(random() * 2);
    awayGoals = homeGoals + 1 + Math.floor(random() * 2);
  }

  const fixedReactionLoss = (options.intensity === "boss" || options.intensity === "derby")
    && options.expectedResult === "loss";
  const playerGoalTarget = fixedReactionLoss
    ? 0
    : sampleProduction(production.goalsPer90 * participationFactor, random);
  const playerAssistTarget = fixedReactionLoss
    ? 0
    : sampleProduction(
        (production.assistsPer90 + (options.assistBonusPercentagePoints ?? 0) / 100) * participationFactor,
        random,
      );
  const minimumHomeGoals = playerGoalTarget + playerAssistTarget;
  if (homeGoals < minimumHomeGoals) {
    homeGoals = minimumHomeGoals;
    if (options.expectedResult === "draw") awayGoals = homeGoals;
    if (options.expectedResult === "loss") awayGoals = Math.max(awayGoals, homeGoals + 1);
  }

  const home = createTeam("home", options, random);
  const away = createTeam("away", options, random);
  const events: MatchEvent[] = [];
  const usedMinutes = new Set<number>();
  const getMinute = () => {
    let minute = 5 + Math.floor(random() * 83);
    while (usedMinutes.has(minute)) minute = 5 + Math.floor(random() * 83);
    usedMinutes.add(minute);
    return minute;
  };
  const attackingPlayer = (team: MatchParticipant[], favorUser = false) => {
    const outfield = team.slice(1);
    if (favorUser && team === home) {
      const chance = Math.min(.85, production.goalsPer90 / 1.5) * participationFactor;
      const user = outfield.find((player) => player.isUser);
      if (user && random() < chance) return user;
      const teammates = outfield.filter((player) => !player.isUser);
      if (teammates.length) return pick(teammates, random);
    }
    return pick(outfield, random);
  };

  const user = home.find((player) => player.isUser);
  const scoringTeammates = home.slice(1).filter((player) => !player.isUser);
  for (let index = 0; index < homeGoals; index += 1) {
    const scorer = index < playerGoalTarget && user ? user : pick(scoringTeammates, random);
    const candidates = home.slice(1).filter((player) => player !== scorer && !player.isUser);
    let assist = random() < .82 ? pick(candidates, random) : null;
    const teammateGoalIndex = index - playerGoalTarget;
    if (!scorer.isUser && user && teammateGoalIndex >= 0 && teammateGoalIndex < playerAssistTarget) assist = user;
    events.push({ type: "goal", team: "home", minute: getMinute(), actor: scorer.name, assist: assist?.name ?? null });
  }
  for (let index = 0; index < awayGoals; index += 1) {
    const scorer = attackingPlayer(away);
    const candidates = away.slice(1).filter((player) => player !== scorer);
    events.push({
      type: "goal",
      team: "away",
      minute: (options.intensity === "boss" || options.intensity === "derby") && options.expectedResult === "loss" && index === 0 ? 31 : getMinute(),
      actor: scorer.name,
      assist: random() < .75 ? pick(candidates, random).name : null,
    });
  }

  const saveCount = 3 + Math.floor(random() * 3);
  for (let index = 0; index < saveCount; index += 1) {
    const savedByHome = index % 2 === 0;
    const keeper = savedByHome ? home[0]! : away[0]!;
    const shooter = attackingPlayer(savedByHome ? away : home, !savedByHome);
    events.push({
      type: "save",
      team: savedByHome ? "home" : "away",
      minute: getMinute(),
      actor: keeper.name,
      shooter: shooter.name,
    });
  }

  const foulCount = 2 + Math.floor(random() * 3);
  for (let index = 0; index < foulCount; index += 1) {
    const byHome = random() < .5;
    events.push({
      type: "foul",
      team: byHome ? "home" : "away",
      minute: getMinute(),
      actor: pick((byHome ? home : away).slice(1), random).name,
      victim: pick((byHome ? away : home).slice(1), random).name,
      card: random() < .42,
    });
  }

  events.sort((left, right) => left.minute - right.minute);
  const durationMs = durationFor(options);
  return {
    matchId: options.matchId,
    opponent: options.opponent,
    events,
    durationMs,
    lateReactionMinute,
    homeGoals,
    awayGoals,
    playerGoals: events.filter((event) => event.type === "goal" && event.actor === options.player.name).length,
    playerAssists: events.filter((event) => event.type === "goal" && event.assist === options.player.name).length,
    playerMinutes: options.playerMinutes ?? 90,
    outcome: options.expectedResult,
  };
}

/** Converts a late boss or derby reaction into a coherent final scoreline and timeline. */
export function buildBossComebackSummary(simulation: MatchSimulation, options: MatchOptions): MatchSummary {
  const usedMinutes = new Set(simulation.events.map((event) => event.minute));
  const nextAvailableMinute = (preferred: number) => {
    let minute = preferred;
    while (usedMinutes.has(minute)) minute += 1;
    usedMinutes.add(minute);
    return minute;
  };
  const homeSquad = options.homeSquadNames ?? getPlayerRoster(options.homeTeam) ?? TEAMMATES;
  const teammate = homeSquad.slice(1).find((name) => name !== options.player.name) ?? TEAMMATES[1];
  const comebackGoals: MatchEvent[] = [
    { type: 'goal', team: 'home', minute: nextAvailableMinute(84), actor: teammate, assist: options.player.name },
    { type: 'goal', team: 'home', minute: nextAvailableMinute(89), actor: options.player.name, assist: null },
  ];
  const events = [...simulation.events, ...comebackGoals].sort((left, right) => left.minute - right.minute);
  return {
    ...simulation,
    homeGoals: simulation.awayGoals + 1,
    playerGoals: simulation.playerGoals + 1,
    playerAssists: simulation.playerAssists + 1,
    outcome: 'win',
    events,
  };
}
